/**
 * LoaderPreview — página de validación visual del loader rediseñado.
 * Ruta pública: /dev/loader
 * Eliminar antes de producción.
 */
import { useState } from 'react';
import { UrbanNetworkLoader } from '../core/layouts/components/UrbanNetworkLoader';
import { UrbanNetworkLoaderV2 } from '../core/layouts/components/UrbanNetworkLoaderV2';
import { UrbanNetworkLoaderV3 } from '../core/layouts/components/UrbanNetworkLoaderV3';
import { UrbanNetworkLoaderV4 } from '../core/layouts/components/UrbanNetworkLoaderV4';

type Size = 'xs' | 'sm' | 'md' | 'xl';
const SIZES: Size[] = ['xs', 'sm', 'md', 'xl'];

export function LoaderPreview(): JSX.Element {
  const [showW, setShowW] = useState(false);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0f1a',
      color: '#8ab4c8',
      fontFamily: 'monospace',
      padding: '40px 32px',
      boxSizing: 'border-box',
    }}>
      <h2 style={{ color: '#2ab8e8', marginBottom: 4, fontSize: 14, fontWeight: 600, letterSpacing: 2 }}>
        WESTEER — LOADER PREVIEW
      </h2>
      <p style={{ fontSize: 11, opacity: 0.5, marginBottom: 40 }}>
        /dev/loader · solo desarrollo
      </p>

      {/* Toggle showW */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, marginBottom: 48, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={showW}
          onChange={e => setShowW(e.target.checked)}
          style={{ accentColor: '#2ab8e8' }}
        />
        <span>
          Mostrar overlay W <span style={{ opacity: 0.5 }}>(aristas finales a baja opacidad — para verificar que UP[] no prefigura la W)</span>
        </span>
      </label>

      {/* Grid de tamaños */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 32, marginBottom: 64 }}>
        {SIZES.map(size => (
          <div key={size} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 1 }}>{size}</span>

            {/* v4 — íconos → red → W */}
            <div style={{
              background: '#0d1520',
              border: '1px solid #1a2a38',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: size === 'xl' ? 24 : 20,
              minWidth: size === 'xl' ? 320 : size === 'md' ? 200 : size === 'sm' ? 140 : 80,
              minHeight: size === 'xl' ? 200 : size === 'md' ? 130 : size === 'sm' ? 90 : 56,
            }}>
              <UrbanNetworkLoaderV4 size={size} />
            </div>
            <span style={{ fontSize: 10, color: '#7dffd4', opacity: 0.9 }}>v4 — íconos → red → W</span>

            {/* v3 — trama + íconos */}
            <div style={{
              background: '#0d1520',
              border: '1px solid #1a2a38',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: size === 'xl' ? 24 : 20,
              minWidth: size === 'xl' ? 320 : size === 'md' ? 200 : size === 'sm' ? 140 : 80,
              minHeight: size === 'xl' ? 200 : size === 'md' ? 130 : size === 'sm' ? 90 : 56,
            }}>
              <UrbanNetworkLoaderV3 size={size} />
            </div>
            <span style={{ fontSize: 10, color: '#4dd8ff', opacity: 0.9 }}>v3 — íconos urbanos</span>

            {/* v2 — trama urbana */}
            <div style={{
              background: '#0d1520',
              border: '1px solid #1a2a38',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: size === 'xl' ? 24 : 20,
              minWidth: size === 'xl' ? 320 : size === 'md' ? 200 : size === 'sm' ? 140 : 80,
              minHeight: size === 'xl' ? 200 : size === 'md' ? 130 : size === 'sm' ? 90 : 56,
            }}>
              <UrbanNetworkLoaderV2 size={size} showW={showW} />
            </div>
            <span style={{ fontSize: 10, color: '#2ab8e8', opacity: 0.7 }}>v2 — trama urbana</span>

            {/* v1 — original (referencia) */}
            <div style={{
              background: '#0d1520',
              border: '1px solid #1a2a38',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: size === 'xl' ? 24 : 20,
              minWidth: size === 'xl' ? 320 : size === 'md' ? 200 : size === 'sm' ? 140 : 80,
              minHeight: size === 'xl' ? 200 : size === 'md' ? 130 : size === 'sm' ? 90 : 56,
            }}>
              <UrbanNetworkLoader size={size} />
            </div>
            <span style={{ fontSize: 10, opacity: 0.35 }}>v1 — original</span>
          </div>
        ))}
      </div>

      {/* Panel de diagnóstico */}
      <div style={{ fontSize: 11, opacity: 0.45, lineHeight: 1.8, maxWidth: 600 }}>
        <p style={{ marginBottom: 4 }}>Qué validar en esta fase (v2 estático):</p>
        <ul style={{ paddingLeft: 16, margin: 0 }}>
          <li>¿md sin overlay lee como trama urbana, no como logo?</li>
          <li>¿Con overlay, los nodos están claramente lejos de la W?</li>
          <li>¿xl tiene densidad correcta, sin saturación cyan?</li>
          <li>¿xs solo muestra nodos (calles ocultas)?</li>
        </ul>
      </div>
    </div>
  );
}
